'use client';

import type { ReactNode } from 'react';
import { useEffect, useLayoutEffect, useState } from 'react';
import {
  ToasterProvider,
  toasterApi,
  ChatMentionsProvider,
  Spinner,
  configureMessageHistoryPersistence,
} from '@terra/ui-design-system';
import {
  setTerraAuthHeaderProvider,
  setTerraConfigProvider,
} from '@terra/shared-utils';
import { Flex } from '@terra/ui-styled-system';
import { UseIntlProvider } from './i18n/UseIntlProvider';
import { AuthProvider } from './providers/auth';
import { AuthLayout } from '../components/AppLayout';
import { NavigationProvider } from '../contexts/NavigationContext';
import { ProjectProvider } from '../contexts/ProjectContext';
import { QueryProvider } from './providers/QueryProvider';
import { FeatureFlagsProvider } from './providers/FeatureFlagsProvider';
import { SSEProvider } from './providers/SseProvider';
import { HttpReplayProvider } from './providers/HttpReplayProvider';
import { CliReplayProvider } from './providers/CliReplayProvider';
import { HttpComparerProvider } from './providers/HttpComparerProvider';
import { HttpSendToChatProvider } from './providers/HttpSendToChatProvider';
import { TraceEntityNavigationProvider } from './providers/TraceEntityNavigationProvider';
import { InteractSessionProvider } from './providers/InteractSessionProvider';
import { StreamingSessionProvider } from './providers/StreamingSessionProvider';
import { PortalToolProvider } from './providers/portal-tools';
import { BackgroundExecutionProvider } from './providers/BackgroundExecutionProvider';
import {
  getTenantStorageKey,
  initTenantIdFromJwt,
} from '../store/tenantStorage';
import { rehydrateTenantScopedStores } from '../store/rehydrateTenantScopedStores';
import { useTargetId, useTargetType } from '../store/projectStore';
import { getWorkerManager } from '../workers';

import '../utils/memoryMonitor';
import '../utils/autoLeakDetector';
import { BoothTour } from '../components/booth/BoothTour';
import { ChromiumUpdateModal } from '../components/ChromiumUpdateModal';
import { TermsAndConditionsModal } from '../components/TermsAndConditionsModal';
import { HybridClientThemeProvider } from '../components/theme/HybridClientThemeProvider';
import { useMaintenanceActions } from '../hooks/useMaintenanceActions';
import { useToolHarSSE } from '../hooks/useToolHarSSE';
import { useFindingsUpdatedSSE } from '../hooks/useFindingsUpdatedSSE';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { TelemetryProvider } from '../telemetry/TelemetryProvider';
import { BIEventsProvider } from '../telemetry/BIEventsProvider';
import { getConfigValue } from '../lib/public-env/get-config-value';
import { BrandingInitProvider } from './providers/BrandingInitProvider';
import { setupCrossWindowSync } from '../store/crossWindowSync';
import { useDetachedWindowLifecycle } from '../hooks/useDetachedWindowLifecycle';
import { IpAllowlistDeniedGate } from '../components/IpAllowlistDeniedGate';
import { QuitConfirmationPrompt } from '../components/QuitConfirmationPrompt';

export { toasterApi };

configureMessageHistoryPersistence({
  getStorageKey: () => getTenantStorageKey('terra-chat-message-history'),
});

function SharedUtilsSetup() {
  useLayoutEffect(() => {
    setTerraConfigProvider((key) =>
      getConfigValue(key as Parameters<typeof getConfigValue>[0]),
    );

    return () => {
      setTerraConfigProvider(null);
    };
  }, []);

  useEffect(() => {
    getWorkerManager()?.warmup(['http-parser', 'body-processor', 'search']);

    if (window.electronAPI) {
      setTerraAuthHeaderProvider(() => undefined);
    }

    return () => {
      setTerraAuthHeaderProvider(null);
    };
  }, []);

  return null;
}

function MaintenanceActionsHandler() {
  useMaintenanceActions();

  return null;
}

function ToolHarSSEHandler() {
  const targetId = useTargetId();
  const targetType = useTargetType();

  useToolHarSSE({
    targetId: targetId || '',
    targetType,
    enabled: !!targetId,
  });

  return null;
}

function FindingsUpdatedSSEHandler() {
  useFindingsUpdatedSSE();

  return null;
}

function DetachedWindowHandlers() {
  useDetachedWindowLifecycle();

  useEffect(() => {
    return setupCrossWindowSync();
  }, []);

  return null;
}

function TenantStorageInitializer({ children }: { children: ReactNode }) {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void initTenantIdFromJwt()
      .then(() => rehydrateTenantScopedStores())
      .catch((error: unknown) => {
        console.warn(
          '[TenantStorageInitializer] Failed to initialize tenant storage',
          {
            error: error instanceof Error ? error : new Error(String(error)),
          },
        );
      })
      .finally(() => {
        if (!cancelled) {
          setIsReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const MIGRATION_KEY = 'terra-proxy-body-migration-v1';

    if (localStorage.getItem(MIGRATION_KEY)) return;

    if (typeof indexedDB !== 'undefined' && indexedDB.databases) {
      indexedDB
        .databases()
        .then((databases) => {
          const bodyDbs = databases.filter(
            (db) => db.name && db.name.startsWith('terra-proxy-bodies-'),
          );

          for (const db of bodyDbs) {
            if (db.name) {
              indexedDB.deleteDatabase(db.name);
            }
          }

          localStorage.setItem(MIGRATION_KEY, String(Date.now()));
        })
        .catch((error: unknown) => {
          console.warn(
            '[TenantStorageInitializer] Failed to migrate legacy body databases',
            {
              error: error instanceof Error ? error : new Error(String(error)),
            },
          );
          localStorage.setItem(MIGRATION_KEY, String(Date.now()));
        });
    } else {
      localStorage.setItem(MIGRATION_KEY, String(Date.now()));
    }
  }, []);

  if (!isReady) {
    return (
      <Flex height="100vh" justify="center" align="center" bg="bg.canvas">
        <Spinner size="lg" />
      </Flex>
    );
  }

  return children;
}

export default function Providers({ children }: { children: ReactNode }) {
  return (
    <UseIntlProvider locale="en">
      <BrandingInitProvider>
        <SharedUtilsSetup />
        <HybridClientThemeProvider>
          <TenantStorageInitializer>
            <AuthProvider>
              <QueryProvider>
                <IpAllowlistDeniedGate />
                <TelemetryProvider />
                <BIEventsProvider />
                <ErrorBoundary>
                  <FeatureFlagsProvider>
                    <SSEProvider>
                      <PortalToolProvider>
                        <BackgroundExecutionProvider>
                          <ProjectProvider>
                            <NavigationProvider>
                              <ChatMentionsProvider>
                                <HttpReplayProvider>
                                  <CliReplayProvider>
                                    <HttpComparerProvider>
                                      <HttpSendToChatProvider>
                                        <TraceEntityNavigationProvider>
                                          <StreamingSessionProvider>
                                            <AuthLayout>{children}</AuthLayout>
                                            <ToasterProvider />
                                            <QuitConfirmationPrompt />
                                            <BoothTour />
                                            <TermsAndConditionsModal />
                                            <ChromiumUpdateModal />
                                            <MaintenanceActionsHandler />
                                            <FindingsUpdatedSSEHandler />
                                            <ToolHarSSEHandler />
                                            <DetachedWindowHandlers />
                                            <InteractSessionProvider />
                                          </StreamingSessionProvider>
                                        </TraceEntityNavigationProvider>
                                      </HttpSendToChatProvider>
                                    </HttpComparerProvider>
                                  </CliReplayProvider>
                                </HttpReplayProvider>
                              </ChatMentionsProvider>
                            </NavigationProvider>
                          </ProjectProvider>
                        </BackgroundExecutionProvider>
                      </PortalToolProvider>
                    </SSEProvider>
                  </FeatureFlagsProvider>
                </ErrorBoundary>
              </QueryProvider>
            </AuthProvider>
          </TenantStorageInitializer>
        </HybridClientThemeProvider>
      </BrandingInitProvider>
    </UseIntlProvider>
  );
}
